import { useMatches } from '@tanstack/react-router';
import type { WorkflowResponse, WorkflowDefinitionResponse } from '@erp/rpc';
import { useGetWorkflow } from 'src/api/rpc/hooks/workflows/useWorkflowDefinitions';

/**
 * Resolved workflow-viewer context shared by every tab under
 * `/workflows/$workflowId` and `/workflows/$workflowId/v/$versionNumber`.
 *
 * Populated by the layout loaders in `routes.tsx`. Tabs should read from here
 * instead of re-querying off the URL param — the URL param is a *workflow id*,
 * not a definition id, and resolution (pinned vs. specific version) lives in
 * one place.
 *
 * The lineage `workflow` row is merged with the live `getWorkflow` query so
 * mutations (e.g. toggling `isActive`) stay in sync without waiting for a full
 * route reload — loader data alone would stay stale after `setQueryData`.
 *
 * `definition` is null for create-mode (`/workflows/new`) and for workflows
 * with no pinned version yet. `viewingVersionNumber` is null in those same
 * cases; otherwise it's the version being viewed (matching `definition.version`).
 */
export interface WorkflowViewerContext {
  workflow: WorkflowResponse | null;
  definition: WorkflowDefinitionResponse | null;
  viewingVersionNumber: number | null;
  isCreateMode: boolean;
  /** True when the view is explicitly pinned-version (no `/v/N`). */
  isPinnedView: boolean;
}

/** TanStack Router `routeId` strings vary by version (leading slash, pathless segments). Avoid brittle exact equality — see workflows layout in `routes.tsx`. */
function isWorkflowVersionDetailLayout(routeId: string): boolean {
  return routeId.includes('/workflows/$workflowId/v/$versionNumber');
}

export function isWorkflowPinnedDetailLayout(routeId: string): boolean {
  return (
    routeId.includes('/workflows/$workflowId') &&
    !routeId.includes('/workflows/$workflowId/v/')
  );
}

/**
 * Reads the nearest matching layout route's loader data. When viewing a
 * specific version we prefer the inner (version) loader, since its loader
 * resolves to the version-specific definition; otherwise we fall back to the
 * outer (pinned) loader.
 */
export function useWorkflowViewerContext(): WorkflowViewerContext {
  const matches = useMatches();

  const versionMatch = matches.find(
    (m) =>
      typeof m.routeId === 'string' && isWorkflowVersionDetailLayout(m.routeId),
  );
  const pinnedMatch = matches.find(
    (m) =>
      typeof m.routeId === 'string' && isWorkflowPinnedDetailLayout(m.routeId),
  );

  const loaderData = (versionMatch?.loaderData ??
    pinnedMatch?.loaderData ??
    null) as
    | {
        workflow: WorkflowResponse | null;
        definition: WorkflowDefinitionResponse | null;
        viewingVersionNumber: number | null;
      }
    | null
    | { crumb: string }; // create-mode returns only a crumb

  const params = pinnedMatch?.params as { workflowId?: string } | undefined;
  const workflowIdForQuery =
    params?.workflowId && params.workflowId !== 'new'
      ? params.workflowId
      : undefined;

  const { data: workflowFromQuery } = useGetWorkflow(workflowIdForQuery);

  const workflowFromLoader =
    (loaderData && 'workflow' in loaderData ? loaderData.workflow : null) ??
    null;

  const workflow = workflowFromQuery ?? workflowFromLoader;
  const definition =
    (loaderData && 'definition' in loaderData ? loaderData.definition : null) ??
    null;
  const viewingVersionNumber =
    (loaderData && 'viewingVersionNumber' in loaderData
      ? loaderData.viewingVersionNumber
      : null) ?? null;

  const isCreateMode = params?.workflowId === 'new';
  const isPinnedView = !versionMatch;

  return {
    workflow,
    definition,
    viewingVersionNumber,
    isCreateMode,
    isPinnedView,
  };
}
